import {createContext, lazy, Suspense, useContext} from 'react'

import type {WebConsoleProps} from './WebConsole'
import type {ComponentType} from 'react'

const WebConsole = lazy(() => import('./WebConsole'))

/**
 * WebConsole이 조상 트리에 이미 마운트되어 있는지 추적하는 Context.
 * withWebConsole HOC가 중첩 적용되는 것을 방지합니다.
 */
const WebConsoleMountedContext = createContext(false)

interface WithWebConsoleProps extends WebConsoleProps {
    enabled: boolean
}

const withWebConsole = <T extends object>(
    Component: ComponentType<T>,
    {enabled, ...webConsoleProps}: WithWebConsoleProps,
) => {
    return (props: T) => {
        const isAlreadyMounted = useContext(WebConsoleMountedContext)

        if (isAlreadyMounted && enabled) {
            // eslint-disable-next-line no-console
            console.warn(
                '[withWebConsole] WebConsole이 이미 조상 컴포넌트에 적용되어 있습니다. 중첩 적용은 무시됩니다. 최상위에서 한 번만 사용하세요.',
            )

            return <Component {...props} />
        }

        return (
            <WebConsoleMountedContext.Provider value>
                <Component {...props} />
                {enabled && (
                    <Suspense fallback={null}>
                        <WebConsole {...webConsoleProps} />
                    </Suspense>
                )}
            </WebConsoleMountedContext.Provider>
        )
    }
}

export default withWebConsole
